import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';
import UserAvatar from '../components/UserAvatar';
import { getProfileUrl, getDisplayHandle } from '../utils/profileUtils';
import { ReactionPicker, ReactionSummary } from '../components/ReactionPicker';
import {
  ArrowLeft, MessageCircle, Share2, Bookmark, MoreHorizontal,
  Play, ChevronLeft, ChevronRight, Volume2, VolumeX, Loader2,
  Send, Clock, Eye, Sparkles, Trash2, Flag, Pencil, Check, X
} from 'lucide-react';
import { AttachmentPreviewCard } from '../components/ContentAttachmentPicker';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

/**
 * PostDetailPage - Full page view for a single post with comments
 */
export default function PostDetailPage() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, getAuthHeaders } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  // Reactions state
  const [myReaction, setMyReaction] = useState(null);
  const [reactions, setReactions] = useState({
    total_count: 0,
    reaction_counts: {},
    top_reactions: []
  });
  const [saved, setSaved] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);
  const [isEditingPost, setIsEditingPost] = useState(false);
  const [editPostContent, setEditPostContent] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');

  useEffect(() => {
    if (postId) {
      fetchPost();
      fetchComments();
      fetchReactions();
    }
  }, [postId]); // eslint-disable-line

  const fetchReactions = async () => {
    try {
      const headers = isAuthenticated ? getAuthHeaders() : {};
      const res = await fetch(`${API}/posts/${postId}/reactions`, { headers });
      if (res.ok) {
        const data = await safeJson(res);
        if (data && data.ok) {
          setReactions({
            total_count: data.total_count || 0,
            reaction_counts: data.reaction_counts || {},
            top_reactions: data.top_reactions || []
          });
          setMyReaction(data.my_reaction || null);
        }
      }
    } catch (err) {
      console.error('Error fetching reactions:', err);
    }
  };

  const fetchPost = async () => {
    try {
      const headers = isAuthenticated ? getAuthHeaders() : {};
      
      // Try to fetch from posts endpoint
      const res = await fetch(`${API}/posts/${postId}`, { headers });
      
      if (res.ok) {
        const result = await safeJson(res);
        // safeJson wraps response in { ok, status, data }
        // API returns { ok: true, post: {...} }
        if (result.ok && result.data) {
          const postData = result.data.post || result.data;
          setPost(postData);
          setSaved(postData.saved_by_me || false);
        } else {
          // Post not found
          toast.error('Post not found');
          navigate('/feed');
        }
      } else if (res.status === 404) {
        toast.error('Post not found or has been removed');
        navigate('/feed');
      } else {
        throw new Error('Failed to load post');
      }
    } catch (err) {
      console.error('Error fetching post:', err);
      toast.error('Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const headers = isAuthenticated ? getAuthHeaders() : {};
      const res = await fetch(`${API}/posts/${postId}/comments?limit=100`, { headers });
      
      if (res.ok) {
        const result = await safeJson(res);
        // safeJson wraps response in { ok, status, data }
        if (result.ok && result.data) {
          const commentsData = result.data.comments || (Array.isArray(result.data) ? result.data : []);
          setComments(commentsData);
        }
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setLoadingComments(false);
    }
  };

  // Handle reaction selection
  const handleReaction = async (reaction) => {
    if (!isAuthenticated) {
      toast.info('Sign in to react to posts');
      navigate('/login');
      return;
    }

    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      
      if (reaction === null) {
        // Remove reaction
        await fetch(`${API}/posts/${postId}/react`, {
          method: 'DELETE',
          headers
        });
        
        setMyReaction(null);
        setReactions(prev => ({
          ...prev,
          total_count: Math.max(0, prev.total_count - 1)
        }));
      } else {
        // Add or change reaction
        await fetch(`${API}/posts/${postId}/react`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ reaction })
        });
        
        const hadReaction = !!myReaction;
        setMyReaction(reaction);
        
        if (!hadReaction) {
          setReactions(prev => ({
            ...prev,
            total_count: prev.total_count + 1
          }));
        }
      }
    } catch (err) {
      console.error('Reaction error:', err);
      toast.error('Failed to react to post');
    }
  };

  const handleSave = async () => {
    if (!isAuthenticated) {
      toast.info('Sign in to save posts');
      navigate('/login');
      return;
    }

    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const endpoint = saved ? 'unsave' : 'save';
      
      await fetch(`${API}/posts/${postId}/${endpoint}`, {
        method: 'POST',
        headers
      });
      
      setSaved(!saved);
      toast.success(saved ? 'Removed from saved' : 'Saved!');
    } catch (err) {
      toast.error('Failed to save post');
    }
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/post/${postId}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Post by ${post?.author_name || 'DMV Artist'}`,
          text: post?.content?.substring(0, 100) || 'Check out this post!',
          url: shareUrl
        });
      } catch (err) {
        // User cancelled
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied!');
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.info('Sign in to comment');
      navigate('/login');
      return;
    }

    if (!newComment.trim()) {
      return;
    }

    setSubmittingComment(true);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const res = await fetch(`${API}/posts/${postId}/comments`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ comment_text: newComment.trim() })
      });

      if (res.ok) {
        setNewComment('');
        toast.success('Comment posted!');
        fetchComments(); // Refresh comments
      } else {
        const data = await safeJson(res);
        toast.error(data?.detail || 'Failed to post comment');
      }
    } catch (err) {
      toast.error('Failed to post comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeletePost = async () => {
    if (!confirm('Delete this post? This cannot be undone.')) return;
    
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API}/posts/${postId}`, {
        method: 'DELETE',
        headers
      });
      
      if (res.ok) {
        toast.success('Post deleted');
        navigate('/feed');
      } else {
        toast.error('Failed to delete post');
      }
    } catch (err) {
      toast.error('Failed to delete post');
    }
  };

  const handleEditPost = async () => {
    if (!editPostContent.trim()) return;
    try {
      const res = await fetch(`${API}/posts/${postId}/edit`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ content_text: editPostContent.trim() }),
      });
      if (res.ok) {
        setPost(prev => ({ ...prev, content: editPostContent.trim(), content_text: editPostContent.trim(), is_edited: true }));
        setIsEditingPost(false);
        toast.success('Post updated');
      } else {
        toast.error('Failed to update post');
      }
    } catch { toast.error('Failed to update post'); }
  };

  const handleEditComment = async (commentId) => {
    if (!editCommentText.trim()) return;
    try {
      const res = await fetch(`${API}/posts/${postId}/comments/${commentId}/edit`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ comment_text: editCommentText.trim() }),
      });
      if (res.ok) {
        setComments(prev => prev.map(c =>
          (c.comment_id || c._id) === commentId
            ? { ...c, comment_text: editCommentText.trim(), content: editCommentText.trim(), is_edited: true }
            : c
        ));
        setEditingCommentId(null);
        setEditCommentText('');
        toast.success('Comment updated');
      } else {
        toast.error('Failed to update comment');
      }
    } catch { toast.error('Failed to update comment'); }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const isVideo = (url) => {
    return url?.match(/\.(mp4|webm|mov)$/i) || url?.includes('video');
  };

  const nextMedia = () => {
    if (!post?.media_urls) return;
    setCurrentMediaIndex(prev => (prev + 1) % post.media_urls.length);
  };

  const prevMedia = () => {
    if (!post?.media_urls) return;
    setCurrentMediaIndex(prev => (prev - 1 + post.media_urls.length) % post.media_urls.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading post...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="glass-card p-8 text-center max-w-md">
          <h2 className="text-xl font-bold mb-2">Post Not Found</h2>
          <p className="text-muted-foreground mb-4">This post may have been deleted or is no longer available.</p>
          <Button onClick={() => navigate('/feed')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Feed
          </Button>
        </Card>
      </div>
    );
  }

  const mediaItems = post.media_urls || [];
  const hasMultipleMedia = mediaItems.length > 1;
  const isOwner = user?.user_id === post.user_id || 
                  user?.user_id === post.artist_id ||
                  user?.artist_id === post.artist_id;
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const profileUrl = getProfileUrl(post);

  return (
    <div className="min-h-screen pb-20" data-testid="post-detail-page">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-semibold">Post</h1>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4">
        {/* Post Card */}
        <Card className="glass-card border-border overflow-hidden mb-6" data-testid="post-detail-card">
          {/* Header - Author Info */}
          <div className="p-4 pb-3">
            <div className="flex items-center justify-between">
              <Link to={profileUrl} className="flex items-center gap-3 group/author">
                <div className="relative">
                  <UserAvatar 
                    src={post.author_avatar || post.artist_avatar}
                    alt={post.author_name || post.artist_name}
                    size="md"
                    className="ring-2 ring-transparent group-hover/author:ring-primary transition-all"
                  />
                  {post.is_verified && (
                    <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                      <Sparkles className="w-2.5 h-2.5 text-white" />
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-white group-hover/author:text-primary transition-colors">
                      {post.author_name || post.artist_name || 'Artist'}
                    </span>
                    {post.author_tier === 'label' && (
                      <Badge variant="secondary" className="text-xs py-0 px-1.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                        PRO
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>{getDisplayHandle(post)}</span>
                    <span className="text-xs">•</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatTime(post.created_at)}
                    </span>
                  </div>
                </div>
              </Link>

              {/* More Options */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={handleShare}>
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-yellow-500">
                    <Flag className="w-4 h-4 mr-2" />
                    Report
                  </DropdownMenuItem>
                  {(isOwner || isAdmin) && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => { setIsEditingPost(true); setEditPostContent(post.content || post.content_text || ''); }}
                        data-testid="edit-post-detail-btn"
                      >
                        <Pencil className="w-4 h-4 mr-2" />
                        Edit Post
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleDeletePost} className="text-red-500">
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Post
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Content Text */}
          {isEditingPost ? (
            <div className="px-4 pb-3" data-testid="edit-post-detail-form">
              <textarea
                className="w-full bg-muted/30 border border-border rounded-lg p-3 text-[15px] leading-relaxed resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
                rows={4}
                value={editPostContent}
                onChange={(e) => setEditPostContent(e.target.value)}
                autoFocus
                data-testid="edit-post-detail-textarea"
              />
              <div className="flex items-center gap-2 mt-2">
                <Button size="sm" onClick={handleEditPost} data-testid="save-edit-post-btn">
                  <Check className="w-3 h-3 mr-1" /> Save
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setIsEditingPost(false)} data-testid="cancel-edit-post-btn">
                  <X className="w-3 h-3 mr-1" /> Cancel
                </Button>
              </div>
            </div>
          ) : (post.content || post.content_text) ? (
            <div className="px-4 pb-3">
              <p className="text-[15px] leading-relaxed whitespace-pre-wrap">
                {post.content || post.content_text}
              </p>
              {post.is_edited && (
                <span className="text-xs text-muted-foreground mt-1 block">(edited)</span>
              )}
            </div>
          ) : null}

          {/* Content Attachment - Display attached song/video/creator */}
          {post.attachment_type && post.attachment_id && (
            <div className="px-4 pb-3" data-testid="post-attachment">
              <AttachmentPreviewCard
                attachment={{
                  attachmentType: post.attachment_type,
                  attachmentId: post.attachment_id,
                  attachmentOwnerId: post.attachment_owner_id,
                  attachmentTitle: post.attachment_metadata?.title || 'Attached Content',
                  attachmentSubtitle: post.attachment_metadata?.subtitle || post.attachment_metadata?.owner_name || '',
                  attachmentCoverUrl: post.attachment_metadata?.cover_url,
                  attachmentDuration: post.attachment_metadata?.duration,
                  attachmentPlayUrl: post.attachment_metadata?.play_url,
                  attachmentOwnerName: post.attachment_metadata?.owner_name
                }}
                onRemove={null}
                showRequest={true}
              />
            </div>
          )}

          {/* Media */}
          {mediaItems.length > 0 && (
            <div className="relative bg-black/20">
              <div className="aspect-[4/3] sm:aspect-video relative overflow-hidden">
                {isVideo(mediaItems[currentMediaIndex]) ? (
                  <video
                    ref={videoRef}
                    src={mediaItems[currentMediaIndex]}
                    className="w-full h-full object-cover"
                    loop
                    muted={muted}
                    playsInline
                    controls
                    onClick={() => { const v = videoRef.current; if (v) { v.paused ? v.play().catch(() => {}) : v.pause(); } }}
                  />
                ) : (
                  <img 
                    src={mediaItems[currentMediaIndex]} 
                    alt=""
                    className="w-full h-full object-cover"
                  />
                )}

                {/* Video Controls */}
                {isVideo(mediaItems[currentMediaIndex]) && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute bottom-3 right-3 bg-background/50 hover:bg-background/70 h-8 w-8"
                    onClick={(e) => { e.stopPropagation(); setMuted(!muted); }}
                  >
                    {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </Button>
                )}

                {/* Media Navigation */}
                {hasMultipleMedia && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-background/50 hover:bg-background/70 h-8 w-8"
                      onClick={(e) => { e.stopPropagation(); prevMedia(); }}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-background/50 hover:bg-background/70 h-8 w-8"
                      onClick={(e) => { e.stopPropagation(); nextMedia(); }}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                    
                    {/* Dots indicator */}
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                      {mediaItems.map((_, idx) => (
                        <div 
                          key={idx}
                          className={`w-1.5 h-1.5 rounded-full transition-all ${
                            idx === currentMediaIndex ? 'bg-white w-4' : 'bg-muted/300'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Engagement Bar */}
          <div className="p-4 pt-3">
            {/* Stats Row - Show reaction summary */}
            <div className="flex items-center justify-between text-sm text-muted-foreground mb-3">
              <div className="flex items-center gap-4">
                {reactions.total_count > 0 && (
                  <ReactionSummary 
                    reactions={reactions}
                    onClick={() => {}} // TODO: open reaction details modal
                  />
                )}
                {comments.length > 0 && (
                  <span>{comments.length} {comments.length === 1 ? 'comment' : 'comments'}</span>
                )}
              </div>
              {post.views > 0 && (
                <span className="flex items-center gap-1">
                  <Eye className="w-3.5 h-3.5" />
                  {post.views}
                </span>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between border-t border-border pt-3">
              <div className="flex items-center gap-1">
                {/* Reaction Picker (replaces simple Like button) */}
                <ReactionPicker
                  selectedReaction={myReaction}
                  topReactions={reactions.top_reactions}
                  onSelect={handleReaction}
                  disabled={!isAuthenticated}
                />
                
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="gap-2 text-primary"
                  data-testid="comment-button"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span className="hidden sm:inline">Comment</span>
                </Button>
                
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleShare}
                  className="gap-2 hover:text-green-500 hover:scale-105 transition-all duration-200"
                  data-testid="share-button"
                >
                  <Share2 className="w-5 h-5" />
                  <span className="hidden sm:inline">Share</span>
                </Button>
              </div>
              
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleSave}
                className={`transition-all duration-200 hover:scale-105 ${saved ? 'text-yellow-500' : 'hover:text-yellow-500'}`}
                data-testid="save-button"
              >
                <Bookmark className={`w-5 h-5 ${saved ? 'fill-current' : ''}`} />
              </Button>
            </div>
          </div>
        </Card>

        {/* Comments Section */}
        <Card className="glass-card border-border overflow-hidden" data-testid="comments-section">
          <div className="p-4 border-b border-border">
            <h2 className="font-semibold flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Comments ({comments.length})
            </h2>
          </div>

          {/* Comment Input */}
          {isAuthenticated && (
            <form onSubmit={handleSubmitComment} className="p-4 border-b border-border">
              <div className="flex gap-3">
                <UserAvatar 
                  src={user?.picture || user?.avatar_url}
                  alt={user?.name}
                  size="sm"
                />
                <div className="flex-1">
                  <Textarea
                    placeholder="Write a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="min-h-[80px] bg-muted/30 border-border resize-none"
                    data-testid="comment-input"
                  />
                  <div className="flex justify-end mt-2">
                    <Button 
                      type="submit" 
                      size="sm" 
                      disabled={!newComment.trim() || submittingComment}
                      data-testid="submit-comment-button"
                    >
                      {submittingComment ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Post
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          )}

          {/* Comments List */}
          <div className="divide-y divide-border">
            {loadingComments ? (
              <div className="p-8 text-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" />
              </div>
            ) : comments.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No comments yet. Be the first to comment!</p>
              </div>
            ) : (
              comments.map((comment) => {
                const commentId = comment.comment_id || comment._id;
                const isCommentAuthor = user?.user_id === comment.user_id;
                const isEditingThis = editingCommentId === commentId;

                return (
                <div key={commentId} className="p-4" data-testid="comment-item">
                  <div className="flex gap-3">
                    <Link to={getProfileUrl(comment)}>
                      <UserAvatar 
                        src={comment.author_avatar || comment.user_avatar}
                        alt={comment.author_name || comment.user_name}
                        size="sm"
                      />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Link 
                            to={getProfileUrl(comment)} 
                            className="font-medium hover:text-primary transition-colors"
                          >
                            {comment.author_name || comment.user_name || 'User'}
                          </Link>
                          <span className="text-xs text-muted-foreground">
                            {formatTime(comment.created_at)}
                          </span>
                          {comment.is_edited && (
                            <span className="text-xs text-muted-foreground">(edited)</span>
                          )}
                        </div>
                        {(isCommentAuthor || isAdmin) && !isEditingThis && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-6 w-6" data-testid={`comment-menu-${commentId}`}>
                                <MoreHorizontal className="w-3.5 h-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-36">
                              <DropdownMenuItem
                                onClick={() => { setEditingCommentId(commentId); setEditCommentText(comment.content || comment.comment_text || ''); }}
                                data-testid={`edit-comment-${commentId}`}
                              >
                                <Pencil className="w-3.5 h-3.5 mr-2" />
                                Edit
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                      {isEditingThis ? (
                        <div data-testid="edit-comment-form">
                          <textarea
                            className="w-full bg-muted/30 border border-border rounded-lg p-2 text-sm resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            rows={2}
                            value={editCommentText}
                            onChange={(e) => setEditCommentText(e.target.value)}
                            autoFocus
                            data-testid="edit-comment-textarea"
                          />
                          <div className="flex items-center gap-2 mt-1">
                            <Button size="sm" className="h-7 text-xs" onClick={() => handleEditComment(commentId)} data-testid="save-edit-comment-btn">
                              <Check className="w-3 h-3 mr-1" /> Save
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setEditingCommentId(null); setEditCommentText(''); }} data-testid="cancel-edit-comment-btn">
                              <X className="w-3 h-3 mr-1" /> Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">{comment.content || comment.comment_text}</p>
                      )}
                      {/* Comment Attachment */}
                      {comment.attachment_type && comment.attachment_id && (
                        <div className="mt-2">
                          <AttachmentPreviewCard
                            attachment={{
                              attachmentType: comment.attachment_type,
                              attachmentId: comment.attachment_id,
                              attachmentOwnerId: comment.attachment_owner_id,
                              attachmentTitle: comment.attachment_metadata?.title || 'Attached Content',
                              attachmentSubtitle: comment.attachment_metadata?.subtitle || comment.attachment_metadata?.owner_name || '',
                              attachmentCoverUrl: comment.attachment_metadata?.cover_url,
                              attachmentDuration: comment.attachment_metadata?.duration,
                              attachmentPlayUrl: comment.attachment_metadata?.play_url,
                              attachmentOwnerName: comment.attachment_metadata?.owner_name
                            }}
                            onRemove={null}
                            showRequest={true}
                            compact={true}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                );
              })
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
